import { ColDef } from 'ag-grid-community';
import { ColumnDef } from '@tanstack/react-table';
import { faSliders } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ShippingRulePresetApplication } from '../models/ShippingRulePresetApplication';

// Hook que centraliza la definición de columnas de la tabla AG Grid de historial de aplicaciones
export const useHistoryAgGridColumns = (selectedColumns: string[] = []) => {
  const { t } = useTranslation('shippingRulePresetsModule');

  const allColumnDefs: ColDef<ShippingRulePresetApplication>[] = useMemo(
    () => [
      {
        colId: 'preset',
        field: 'metadata.preset_name',
        headerName: t('history.columns.preset'),
        sortable: false,
        flex: 2,
        minWidth: 150,
        cellRenderer: (params: any) => {
          const presetName = params.data?.metadata?.preset_name ?? '-';
          return (
            <div className="flex items-center gap-2 min-w-0">
              <FontAwesomeIcon icon={faSliders} className="w-3.5 h-3.5 text-icons flex-shrink-0" />
              <span className="truncate text-sm text-title">{presetName}</span>
            </div>
          );
        },
      },
      {
        colId: 'targetTeam',
        field: 'target_team',
        headerName: t('history.columns.targetTeam'),
        sortable: false,
        flex: 2,
        minWidth: 150,
        valueGetter: (params) => params.data?.target_team?.name ?? '-',
      },
      {
        colId: 'appliedBy',
        field: 'applied_by_user',
        headerName: t('history.columns.appliedBy'),
        sortable: false,
        flex: 1,
        minWidth: 120,
        valueGetter: (params) => params.data?.applied_by_user?.name ?? '-',
      },
      {
        colId: 'appliedAt',
        field: 'applied_at',
        headerName: t('history.columns.appliedAt'),
        sortable: false,
        flex: 1,
        minWidth: 140,
        valueFormatter: (params) => {
          if (!params.value) return '-';
          const date = new Date(params.value);
          return date.toLocaleDateString('es-ES', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          });
        },
      },
      {
        colId: 'rulesInfo',
        field: 'rules_count',
        headerName: t('history.columns.rulesInfo'),
        sortable: false,
        flex: 2,
        minWidth: 180,
        cellRenderer: (params: any) => {
          if (!params.data) return '-';
          return (
            <span className="text-subtext text-sm">
              {t('history.rulesInfo', {
                applied: params.data.rules_count,
                replaced: params.data.replaced_rules_count,
              })}
            </span>
          );
        },
      },
      {
        colId: 'status',
        field: 'status',
        headerName: t('history.columns.status'),
        sortable: false,
        flex: 1,
        minWidth: 100,
        cellRenderer: (params: any) => {
          if (!params.data) return null;
          const isSuccess = params.data.status === 'success';
          return (
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                isSuccess ? 'bg-bg-success text-bright-success' : 'bg-bg-error text-bright-error'
              }`}
            >
              {isSuccess ? t('history.status.success') : t('history.status.error')}
            </span>
          );
        },
      },
    ],
    [t],
  );

  const tanStackColumns: ColumnDef<ShippingRulePresetApplication>[] = useMemo(
    () => [
      { id: 'preset', accessorKey: 'metadata.preset_name', header: t('history.columns.preset') },
      { id: 'targetTeam', accessorKey: 'target_team.name', header: t('history.columns.targetTeam') },
      { id: 'appliedBy', accessorKey: 'applied_by_user.name', header: t('history.columns.appliedBy') },
      { id: 'appliedAt', accessorKey: 'applied_at', header: t('history.columns.appliedAt') },
      { id: 'rulesInfo', accessorKey: 'rules_count', header: t('history.columns.rulesInfo') },
      { id: 'status', accessorKey: 'status', header: t('history.columns.status') },
    ],
    [t],
  );

  const columnDefs = useMemo(() => {
    if (!selectedColumns || selectedColumns.length === 0) return allColumnDefs;
    return allColumnDefs.filter((col) => col.colId && selectedColumns.includes(String(col.colId)));
  }, [allColumnDefs, selectedColumns]);

  return { columnDefs, tanStackColumns };
};
